/**
 * Formatacao de data, hora e numero. Tudo que vira texto passa por aqui.
 *
 * ── Duas categorias, com regras diferentes ──
 *
 * O que `Intl` resolve — data, hora, moeda — passa a receber a TAG do idioma:
 * "13/08/2026" e "8/13/2026" sao o mesmo instante escrito para leitores
 * diferentes, e errar isso e o tipo de coisa que faz alguem marcar consulta no
 * dia errado.
 *
 * O que e TEXTO — "volta em 3 meses", "ha 5 min" — vem do dicionario, porque
 * traducao nao e formatacao.
 *
 * O que NAO muda de idioma: o dinheiro. Uma atendente brasileira cobra em reais
 * de um medico brasileiro, e mostrar "$1,200.00" para ela em ingles seria trocar
 * a moeda por causa da lingua — que e um erro de outra ordem.
 */

/**
 * `Intl.DateTimeFormat` e caro de construir e barato de reusar. O cache evita
 * recriar seis formatadores a cada render de lista.
 */
type Formatter = Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat;

const cache = new Map<string, Formatter>();

function fmt<T extends Formatter>(chave: string, criar: () => T): T {
  const existente = cache.get(chave);
  if (existente !== undefined) return existente as T;
  const novo = criar();
  cache.set(chave, novo);
  return novo;
}

/**
 * Idioma corrente, lido de fora do React.
 *
 * As funcoes de formatacao sao chamadas de dentro e de fora de componentes —
 * `formatDateFull` aparece em `title=` de tabela, em `map` e em helper puro.
 * Passar o locale por parametro em todas exigiria tocar em cada chamada; um
 * modulo com o valor corrente resolve, e o `I18nProvider` e quem o mantem.
 */
let localeAtual = 'pt-BR';

export function setFormatLocale(tag: string): void {
  localeAtual = tag;
}

/** `12 – 18 de outubro`, ou `October 12 – 18`, no idioma da tela. */
export function formatPeriodo(de: string, ate: string): string {
  return new Intl.DateTimeFormat(localeAtual, {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).formatRange(new Date(`${de}T12:00:00Z`), new Date(`${ate}T12:00:00Z`));
}

/** `Outubro de 2026`, ou `October 2026`. */
export function formatMesAno(data: string): string {
  const s = new Intl.DateTimeFormat(localeAtual, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${data.slice(0, 7)}-01T12:00:00Z`));
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const TIME = () =>
  fmt(
    `time-${localeAtual}`,
    () =>
      new Intl.DateTimeFormat(localeAtual, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
  );

const DATE_SHORT = () =>
  fmt(
    `date_short-${localeAtual}`,
    () =>
      new Intl.DateTimeFormat(localeAtual, {
        day: '2-digit',
        month: '2-digit',
      }),
  );

const DATE_FULL = () =>
  fmt(
    `date_full-${localeAtual}`,
    () =>
      new Intl.DateTimeFormat(localeAtual, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
  );

const MONTH_LONG = () =>
  fmt(
    `month_long-${localeAtual}`,
    () => new Intl.DateTimeFormat(localeAtual, { month: 'long', year: 'numeric' }),
  );

const DAY_LONG = () =>
  fmt(
    `day_long-${localeAtual}`,
    () =>
      new Intl.DateTimeFormat(localeAtual, {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
      }),
  );

const BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

/** Sem centavos: num tile de indicador, ",00" so ocupa espaco e nao informa nada. */
const BRL_ROUND = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});

/**
 * A data de HOJE no fuso de Sao Paulo, em `YYYY-MM-DD` — que e o formato do
 * `<input type="date">` e da coluna `date` do Postgres.
 *
 * `new Date().toISOString().slice(0, 10)` esta ERRADO e o erro e sazonal: das 21h a
 * meia-noite no Brasil, o UTC ja virou o dia seguinte. Um procedimento registrado as
 * 22h ficaria com a data de amanha, e o retorno calculado a partir dele nasceria um
 * dia deslocado. Foi exatamente assim que apareceu no teste desta rota.
 *
 * `sv-SE` porque o locale sueco formata data como `YYYY-MM-DD` nativamente — e evita
 * remontar a string a partir de partes.
 */
const ISO_DIA = new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo' });

/** Mesma conversao para uma data qualquer: ISO completo → `YYYY-MM-DD` local. */
export function toDateInput(iso: string | null): string {
  return iso === null ? '' : ISO_DIA.format(new Date(iso));
}

/**
 * Hoje, no relogio da operacao.
 *
 * Existe para ser a unica resposta a "que dia e hoje" na interface: quatro telas
 * calculavam isso com o `toISOString().slice(0, 10)` que o comentario acima descreve
 * como errado, e usavam o resultado como valor inicial e como minimo de campo de data.
 * Das 21h a meia-noite, a tela de marcacao abria em AMANHA — no horario em que a
 * recepcao remarca o dia seguinte.
 */
export function dataDeHoje(): string {
  return ISO_DIA.format(new Date());
}

/**
 * Quanto tempo o paciente esta esperando resposta, em texto curto.
 *
 * Cortes em 1 h e 4 h porque sao os limites que mudam o que ela FAZ: abaixo de uma
 * hora e fluxo normal, acima de quatro e a conversa que vira reclamacao com o medico.
 */
export function formatWaiting(minutes: number): string {
  if (minutes < 1) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} d`;
}

/** `950` → "R$ 950,00". `null` → "—" (cliente sem mensalidade combinada). */
export function formatBrl(value: number | null): string {
  return value === null ? '—' : BRL.format(value);
}

/** `5440` → "R$ 5.440". Para valores grandes em tiles. */
export function formatBrlRound(value: number): string {
  return BRL_ROUND.format(value);
}

/** `1000` → "1.000" em pt-BR, "1,000" em ingles. Separador de milhar so isso. */
export function formatNumber(value: number): string {
  return fmt(`num-${localeAtual}`, () => new Intl.NumberFormat(localeAtual)).format(value);
}

export function formatTime(iso: string): string {
  return TIME().format(new Date(iso));
}

/**
 * Data de calendario nao e instante, e o navegador nao sabe disso.
 *
 * `new Date('2026-08-24')` e meia-noite UTC. Formatado em Brasilia, que e
 * UTC-3, vira 23/08 — um dia inteiro a menos. Reproduzido: ferias marcadas de
 * 24 a 28 apareciam como 23 a 27 na tela, com o banco guardando o certo.
 *
 * "24 de agosto" e um DIA, nao um ponto no tempo, e por isso e formatado em UTC
 * — o mesmo fuso em que foi interpretado. Instantes de verdade (mensagem,
 * ultima sincronia) continuam no fuso de quem le, que e o certo para eles.
 */
function soData(iso: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso);
}

const UTC = { timeZone: 'UTC' } as const;

export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (!soData(iso)) return DATE_SHORT().format(d);
  return new Intl.DateTimeFormat(localeAtual, {
    day: '2-digit',
    month: '2-digit',
    ...UTC,
  }).format(d);
}

export function formatDateFull(iso: string): string {
  const d = new Date(iso);
  if (!soData(iso)) return DATE_FULL().format(d);
  return new Intl.DateTimeFormat(localeAtual, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...UTC,
  }).format(d);
}

/** "Agosto de 2026" — o mes de referencia de uma cobranca. */
export function formatMonthLong(iso: string): string {
  const text = soData(iso)
    ? new Intl.DateTimeFormat(localeAtual, { month: 'long', year: 'numeric', ...UTC }).format(
        new Date(iso),
      )
    : MONTH_LONG().format(new Date(iso));
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "outubro", a partir de `AAAA-MM`. */
export function formatMonthName(mes: string): string {
  return new Intl.DateTimeFormat(localeAtual, { month: 'long', ...UTC }).format(
    new Date(`${mes}-01T12:00:00Z`),
  );
}

/** "quinta-feira, 13 de agosto" — com a inicial maiuscula, sem virar "13 De Agosto". */
export function formatDayLong(iso: string): string {
  const text = DAY_LONG().format(new Date(iso));
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** `2026-08-13T14:05Z` → "14:05" se hoje, "12/08" se antes. */
export function formatStamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? TIME().format(d) : DATE_SHORT().format(d);
}

/** "agora", "ha 5 min", "ha 2 h", "ha 3 d". */
export function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  // `Intl.RelativeTimeFormat` resolve isto em qualquer idioma sem uma string
  // nossa: "5 min ago" / "há 5 min", com a gramática de cada língua.
  const rel = fmt(
    `rel-${localeAtual}`,
    () => new Intl.RelativeTimeFormat(localeAtual, { numeric: 'auto', style: 'narrow' }),
  );

  if (min < 1) return rel.format(0, 'minute');
  if (min < 60) return rel.format(-min, 'minute');
  const hours = Math.floor(min / 60);
  if (hours < 24) return rel.format(-hours, 'hour');
  const days = Math.floor(hours / 24);
  if (days < 7) return rel.format(-days, 'day');
  return formatDateShort(iso);
}

/** 192 → "3h12". 41 → "41min". */
export function formatCountdown(minutes: number): string {
  if (minutes <= 0) return '0min';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${String(m).padStart(2, '0')}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * `5511912345678` → "+55 11 91234-5678". Aceita com e sem o 55, com e sem `+`.
 *
 * O 55 so e codigo de pais quando sobra um numero brasileiro inteiro depois dele: 55
 * tambem e DDD (Santa Maria, RS), e `55991234567` e um celular de la sem o codigo.
 * Numero de outro pais sai como veio, em vez de ganhar um "+55" que nao e dele.
 */
export function formatPhone(raw: string | null): string {
  if (raw == null) return '—';
  const digits = raw.replace(/\D/g, '');
  if (raw.trim().startsWith('+') && !digits.startsWith('55')) return raw;
  const comPais = digits.startsWith('55') && (digits.length === 12 || digits.length === 13);
  const br = comPais ? digits.slice(2) : digits;
  if (br.length !== 10 && br.length !== 11) return raw;
  const ddd = br.slice(0, 2);
  const rest = br.slice(2);
  const cut = rest.length - 4;
  return `+55 ${ddd} ${rest.slice(0, cut)}-${rest.slice(cut)}`;
}
